import type { AnalyticsPlatform, ContentPost } from 'api/new-analytics/types';
import type { MetricColumnDef } from 'app/modules/analytics/modules/content/utils/contentTableColumns';
import {
  FB_POSTS_COLUMNS,
  FB_REELS_COLUMNS,
  IG_FEED_COLUMNS,
  IG_REELS_COLUMNS,
  IG_STORIES_COLUMNS,
  LI_FEED_COLUMNS,
  TIKTOK_VIDEO_COLUMNS,
} from 'app/modules/analytics/modules/content/utils/contentTableColumns';

export interface ReportPostTableGroup {
  key: string;
  titleKey: string;
  columns: MetricColumnDef[];
  posts: ContentPost[];
}

interface GroupMeta {
  key: string;
  titleKey: string;
  columns: MetricColumnDef[];
}

function resolveGroupMeta(post: ContentPost): GroupMeta | null {
  const platform = post.platform as AnalyticsPlatform;

  switch (platform) {
    case 'facebook':
      return post.entity_type === 'reel'
        ? {
            key: 'facebook_reels',
            titleKey: 'analyticsContent.sections.reels',
            columns: FB_REELS_COLUMNS,
          }
        : {
            key: 'facebook_posts',
            titleKey: 'analyticsContent.sections.postsAndStories',
            columns: FB_POSTS_COLUMNS,
          };
    case 'instagram':
      if (post.entity_type === 'reel') {
        return {
          key: 'instagram_reels',
          titleKey: 'analyticsContent.sections.reels',
          columns: IG_REELS_COLUMNS,
        };
      }
      if (post.entity_type === 'story') {
        return {
          key: 'instagram_stories',
          titleKey: 'analyticsContent.sections.stories',
          columns: IG_STORIES_COLUMNS,
        };
      }
      return {
        key: 'instagram_feed',
        titleKey: 'analyticsContent.sections.feedPosts',
        columns: IG_FEED_COLUMNS,
      };
    case 'linkedin':
      return {
        key: 'linkedin_feed',
        titleKey: 'analyticsContent.sections.feedPosts',
        columns: LI_FEED_COLUMNS,
      };
    case 'tiktok':
      return {
        key: 'tiktok_videos',
        titleKey: 'analyticsContent.sections.videos',
        columns: TIKTOK_VIDEO_COLUMNS,
      };
    default:
      return null;
  }
}

export function buildReportPostTableGroups(
  posts: ContentPost[],
): ReportPostTableGroup[] {
  const groups: ReportPostTableGroup[] = [];
  const groupIndexByKey = new Map<string, number>();

  for (const post of posts) {
    const meta = resolveGroupMeta(post);

    if (!meta) {
      continue;
    }

    const existingIndex = groupIndexByKey.get(meta.key);

    if (existingIndex == null) {
      groupIndexByKey.set(meta.key, groups.length);
      groups.push({
        key: meta.key,
        titleKey: meta.titleKey,
        columns: meta.columns,
        posts: [post],
      });
    } else {
      groups[existingIndex].posts.push(post);
    }
  }

  return groups;
}
